import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import styles from './TickerAutocomplete.module.css';

/**
 * Lightweight ticker autocomplete input.
 * Calls onSelect(symbol) when the user picks a suggestion or presses Enter.
 * Does NOT trigger a screen — just returns the selected symbol string.
 */
export default function TickerAutocomplete({ value, onChange, onSelect, placeholder = 'Ticker or name', className, inputRef }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDrop,    setShowDrop]    = useState(false);
  const [activeIdx,   setActiveIdx]   = useState(-1);

  const debounceRef  = useRef(null);
  const suppressRef  = useRef(false);
  const wrapRef      = useRef(null);

  // Fetch suggestions on value change (debounced 300ms)
  useEffect(() => {
    const q = value.trim();
    if (suppressRef.current) { suppressRef.current = false; return; }
    if (!q) { setSuggestions([]); setShowDrop(false); return; }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get('/api/search', { params: { q } });
        const results = data.results || [];
        setSuggestions(results);
        setShowDrop(results.length > 0);
        setActiveIdx(-1);
      } catch {
        setSuggestions([]);
        setShowDrop(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDrop(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function select(symbol) {
    suppressRef.current = true;
    onChange(symbol);
    setSuggestions([]);
    setShowDrop(false);
    setActiveIdx(-1);
    onSelect(symbol);
  }

  function handleKey(e) {
    if (!showDrop) {
      if (e.key === 'Enter') { const v = value.trim().toUpperCase(); if (v) onSelect(v); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && suggestions[activeIdx]) select(suggestions[activeIdx].symbol);
      else { const v = value.trim().toUpperCase(); if (v) { suppressRef.current = true; setShowDrop(false); onSelect(v); } }
    } else if (e.key === 'Escape') {
      setShowDrop(false);
      setActiveIdx(-1);
    }
  }

  return (
    <div className={`${styles.wrap} ${className || ''}`} ref={wrapRef}>
      <input
        ref={inputRef}
        className={styles.input}
        type="text"
        value={value}
        placeholder={placeholder}
        maxLength={20}
        autoCapitalize="characters"
        autoComplete="off"
        onChange={e => onChange(e.target.value.toUpperCase())}
        onKeyDown={handleKey}
        onFocus={() => suggestions.length > 0 && setShowDrop(true)}
      />
      {showDrop && suggestions.length > 0 && (
        <div className={styles.dropdown}>
          {suggestions.map((s, i) => (
            <div
              key={s.symbol}
              className={`${styles.dropItem} ${i === activeIdx ? styles.dropItemActive : ''}`}
              onMouseDown={() => select(s.symbol)}
            >
              <span className={styles.dropSymbol}>{s.symbol}</span>
              <span className={styles.dropName}>{s.name}</span>
              {s.exchange && <span className={styles.dropMeta}>{s.exchange}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
